import { useState, useEffect } from 'react'
import { AdminLayout } from '../components/layout/AdminLayout'
import { Modal } from '@eshop/shared'
import { kycApi } from '@eshop/shared'
import type { VendorKYC } from '../../types'

export function KYCReviewPage() {
  const [kycs, setKycs] = useState<VendorKYC[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedKyc, setSelectedKyc] = useState<VendorKYC | null>(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    loadPendingKYCs()
  }, [])

  const loadPendingKYCs = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await kycApi.getAllPending()
      setKycs(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar solicitudes KYC')
      console.error('Error loading KYCs:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (kycId: number) => {
    if (!confirm('¿Estás seguro de aprobar esta solicitud KYC?')) return

    try {
      setProcessing(true)
      await kycApi.approveKYC(kycId)
      await loadPendingKYCs()
      setSelectedKyc(null)
      alert('✅ Solicitud KYC aprobada exitosamente')
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al aprobar KYC')
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    if (!selectedKyc || !rejectReason.trim()) {
      alert('Por favor ingresa una razón para el rechazo')
      return
    }

    try {
      setProcessing(true)
      await kycApi.rejectKYC(selectedKyc.id, rejectReason)
      await loadPendingKYCs()
      setSelectedKyc(null)
      setShowRejectModal(false)
      setRejectReason('')
      alert('❌ Solicitud KYC rechazada')
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al rechazar KYC')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando solicitudes KYC...</div>
        </div>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadPendingKYCs}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verificaciones KYC Pendientes</h1>
          <p className="text-sm text-gray-700 mt-1">
            Revisa y aprueba las solicitudes de verificación de vendedores
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-700 uppercase">Pendientes</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{kycs.length}</p>
              </div>
              <div className="text-3xl opacity-70">⏳</div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-700 uppercase">Total Hoy</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {kycs.filter(k => {
                    const today = new Date().toDateString()
                    const submitted = new Date(k.submitted_at || '').toDateString()
                    return today === submitted
                  }).length}
                </p>
              </div>
              <div className="text-3xl opacity-70">📅</div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-purple-700 uppercase">Progreso</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {kycs.length > 0 ? Math.round((kycs.filter(k => k.progress_percentage === 100).length / kycs.length) * 100) : 0}%
                </p>
              </div>
              <div className="text-3xl opacity-70">📊</div>
            </div>
          </div>
        </div>

        {/* KYC List */}
        {kycs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay solicitudes KYC pendientes
            </h3>
            <p className="text-sm text-gray-600">
              Todas las solicitudes han sido procesadas
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Vendedor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Fecha Envío
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Progreso
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {kycs.map((kyc) => (
                  <tr key={kyc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {kyc.personal_info?.full_name || 'Sin nombre'}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {kyc.id}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-700">
                        {kyc.personal_info?.email || 'Sin email'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-700">
                        {kyc.submitted_at
                          ? new Date(kyc.submitted_at).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{ maxWidth: '100px' }}>
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${kyc.progress_percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-700 font-medium">
                          {kyc.progress_percentage}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedKyc(kyc)}
                        className="text-blue-600 hover:text-blue-800 font-medium mr-3"
                      >
                        Ver detalles
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        <Modal
          isOpen={!!selectedKyc}
          onClose={() => setSelectedKyc(null)}
          title="Detalles de Verificación KYC"
          maxWidth="2xl"
        >
          {selectedKyc && (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Personal Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Información Personal</h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Nombre Completo</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedKyc.personal_info?.full_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedKyc.personal_info?.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Teléfono</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedKyc.personal_info?.phone || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Fecha de Nacimiento</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedKyc.personal_info?.date_of_birth || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Información del Negocio</h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Nombre del Negocio</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedKyc.business_info?.business_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tipo de Negocio</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedKyc.business_info?.business_type || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Documentos</h3>
                <div className="bg-gray-50 rounded-lg p-4 mb-3">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Tipo de Documento</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedKyc.document_info?.document_type || selectedKyc.document_type || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Número</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedKyc.document_info?.document_number || selectedKyc.document_number || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Fecha de Expiración</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedKyc.document_info?.document_expiry_date || selectedKyc.document_expiry_date || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-2">ID Frontal</p>
                    {(selectedKyc.document_info?.id_front_url || selectedKyc.document_front_image) ? (
                      <a
                        href={selectedKyc.document_info?.id_front_url || selectedKyc.document_front_image}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={selectedKyc.document_info?.id_front_url || selectedKyc.document_front_image}
                          alt="ID Front"
                          className="w-full h-48 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            target.parentElement!.innerHTML = '<div class="w-full h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">Error al cargar imagen</div>'
                          }}
                        />
                      </a>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">
                        No disponible
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-2">ID Reverso</p>
                    {(selectedKyc.document_info?.id_back_url || selectedKyc.document_back_image) ? (
                      <a
                        href={selectedKyc.document_info?.id_back_url || selectedKyc.document_back_image}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={selectedKyc.document_info?.id_back_url || selectedKyc.document_back_image}
                          alt="ID Back"
                          className="w-full h-48 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            target.parentElement!.innerHTML = '<div class="w-full h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">Error al cargar imagen</div>'
                          }}
                        />
                      </a>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">
                        No disponible
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Address Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Dirección</h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Dirección</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedKyc.address_line1 || 'N/A'}
                      {selectedKyc.address_line2 && `, ${selectedKyc.address_line2}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Ciudad</p>
                    <p className="text-sm font-medium text-gray-900">{selectedKyc.city || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Estado/Provincia</p>
                    <p className="text-sm font-medium text-gray-900">{selectedKyc.state_province || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Código Postal</p>
                    <p className="text-sm font-medium text-gray-900">{selectedKyc.postal_code || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">País</p>
                    <p className="text-sm font-medium text-gray-900">{selectedKyc.country || 'N/A'}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Comprobante de Domicilio</p>
                  {selectedKyc.proof_of_address_image ? (
                    <a
                      href={selectedKyc.proof_of_address_image}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={selectedKyc.proof_of_address_image}
                        alt="Proof of Address"
                        className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          target.parentElement!.innerHTML = '<div class="w-full max-w-md h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">Error al cargar imagen</div>'
                        }}
                      />
                    </a>
                  ) : (
                    <div className="w-full max-w-md h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">
                      No disponible
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => setSelectedKyc(null)}
                  className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={processing}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(true)
                  }}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  disabled={processing}
                >
                  Rechazar
                </button>
                <button
                  onClick={() => handleApprove(selectedKyc.id)}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  disabled={processing}
                >
                  {processing ? 'Procesando...' : 'Aprobar'}
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Reject Modal */}
        <Modal
          isOpen={showRejectModal && !!selectedKyc}
          onClose={() => {
            setShowRejectModal(false)
            setRejectReason('')
          }}
          title="Rechazar Solicitud KYC"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Por favor proporciona una razón para el rechazo. Esta información será enviada al vendedor.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm"
              rows={4}
              placeholder="Ej: Los documentos de identificación están borrosos..."
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectReason('')
                }}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                disabled={processing}
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={processing || !rejectReason.trim()}
              >
                {processing ? 'Procesando...' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  )
}
